import * as React from 'react';
import { useEventCallback } from '@cian/react-utils';
import { ownerDocument, clamp } from '../../../../../internal/utils';
import { doesSupportTouchActionNone, percentToValue, asc, findClosestValueIndex, setValueIndex } from '../../helpers';
import { useFinger } from './useFinger';
export function useMouseTouchController(_ref) {
  let {
    props,
    valueState,
    sliderRef,
    setFocusIndex,
    setActiveIndex,
    setOpenIndex,
    roundValue
  } = _ref;
  const {
    onMouseDown,
    disabled,
    min,
    max
  } = props;
  const {
    valueDerived,
    values,
    changeValue
  } = valueState;
  const [getFinger, setFinger] = useFinger();
  const previousIndex = React.useRef(null);
  const getFingerNewValue = _ref2 => {
    let {
      finger,
      values: values2,
      source,
      move = false
    } = _ref2;
    const {
      current: slider
    } = sliderRef;
    const {
      width,
      left
    } = slider.getBoundingClientRect();
    const percent = (finger.x - left) / width;
    let newValue;
    newValue = percentToValue(percent, min, max);
    newValue = roundValue(newValue);
    newValue = clamp(newValue, min, max);
    let activeIndex = previousIndex.current || 0;
    if (!move) {
      activeIndex = findClosestValueIndex(values2, newValue);
    }
    if (Array.isArray(source)) {
      const previousValue = newValue;
      newValue = setValueIndex({
        values: values2,
        source,
        newValue,
        index: activeIndex
      }).sort(asc);
      activeIndex = newValue.indexOf(previousValue);
      previousIndex.current = activeIndex;
    }
    return {
      newValue,
      activeIndex
    };
  };
  const handleTouchMove = useEventCallback(nativeEvent => {
    const finger = getFinger(nativeEvent);
    if (!finger) {
      return;
    }

    // Cancel move in case some other element consumed a mouseup event and it was not fired.
    if (nativeEvent.type === 'mousemove' && nativeEvent.buttons === 0) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      return handleTouchEnd(nativeEvent);
    }
    const {
      newValue,
      activeIndex
    } = getFingerNewValue({
      finger,
      move: true,
      values,
      source: valueDerived
    });
    setFocusIndex(activeIndex);
    changeValue(newValue);
  });
  const handleTouchEnd = useEventCallback(nativeEvent => {
    setActiveIndex(-1);
    if (nativeEvent.type === 'touchend') {
      setOpenIndex(-1);
    }
    setFinger(null);

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    stopListening();
  });
  const handleTouchStart = useEventCallback(event => {
    // If touch-action: none; is not supported we need to prevent the scroll manually.
    if (!doesSupportTouchActionNone()) {
      event.preventDefault();
    }
    const touch = event.changedTouches[0];
    if (touch) {
      setFinger(event);
    }
    const finger = getFinger(event);
    const {
      newValue,
      activeIndex
    } = getFingerNewValue({
      finger,
      values,
      source: valueDerived
    });
    setFocusIndex(activeIndex);
    changeValue(newValue);
    const doc = ownerDocument(sliderRef.current);
    doc.addEventListener('touchmove', handleTouchMove);
    doc.addEventListener('touchend', handleTouchEnd);
  });
  const stopListening = React.useCallback(() => {
    const doc = ownerDocument(sliderRef.current);
    doc.removeEventListener('mousemove', handleTouchMove);
    doc.removeEventListener('mouseup', handleTouchEnd);
    doc.removeEventListener('touchmove', handleTouchMove);
    doc.removeEventListener('touchend', handleTouchEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleTouchEnd, handleTouchMove]);
  React.useEffect(() => {
    const {
      current: slider
    } = sliderRef;
    if (!slider) {
      return;
    }
    slider.addEventListener('touchstart', handleTouchStart, {
      passive: doesSupportTouchActionNone()
    });
    return () => {
      slider.removeEventListener('touchstart', handleTouchStart, {
        passive: doesSupportTouchActionNone()
      });
      stopListening();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopListening, handleTouchStart]);
  React.useEffect(() => {
    if (disabled) {
      stopListening();
    }
  }, [disabled, stopListening]);
  const handleMouseDown = useEventCallback(event => {
    const finger = getFinger(event.nativeEvent);
    if (onMouseDown) {
      onMouseDown(event);
    }

    // Only handle left clicks
    if (event.button !== 0 || !finger) {
      return;
    }

    // Avoid text selection
    event.preventDefault();
    const {
      newValue,
      activeIndex
    } = getFingerNewValue({
      finger,
      values,
      source: valueDerived
    });
    setFocusIndex(activeIndex);
    changeValue(newValue);
    const doc = ownerDocument(sliderRef.current);
    doc.addEventListener('mousemove', handleTouchMove);
    doc.addEventListener('mouseup', handleTouchEnd);
  });
  return {
    onMouseDown: handleMouseDown
  };
}